/**
 * The RichError class is an error class that can be augmented with a context object.
 */
export class RichError extends Error {
  public readonly context: Record<string, unknown>;

  constructor(message: string, context: Record<string, unknown> = {}, options?: ErrorOptions) {
    super(message, options);
    this.name = "CustomError";
    this.context = context;

    // Maintains proper stack trace for where the error was thrown (only available on V8)
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, RichError);
    }
  }
}

/**
 * UnrecoverableError error class for unrecoverable flow failures.
 * Inherits from CustomError and indicates that the error cannot be recovered from.
 */
export class UnrecoverableError extends RichError {
  constructor(message: string, context: Record<string, unknown> = {}) {
    super(message, context);
    this.name = "UnrecoverableError";

    // Maintains proper stack trace for where the error was thrown (only available on V8)
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, UnrecoverableError);
    }
  }
}

export abstract class HttpError extends Error {
  statusCode = 500;
  name = "HttpError";
  /**
   * Machine-readable refusal code, serialized by `endpoint()` as `code` beside the message.
   *
   * Set it when a caller has to branch on *why* a request was refused and the status alone cannot
   * say — several unrelated refusals share one status. Leave it unset otherwise: a client that
   * only renders the message needs nothing here.
   *
   * Named for the refusal rather than the wire key because `code` is already taken on a subclass
   * for an unrelated vendor error number.
   */
  refusalCode?: string;

  constructor(
    message: string,
    public originalError?: Error,
  ) {
    super(message);
    this.originalError = originalError;
  }
}

export class BadRequestError extends HttpError {
  statusCode = 400;
  name = "BadRequestError";
}

export class UnauthorizedError extends HttpError {
  statusCode = 401;
  name = "UnauthorizedError";
}

export class ForbiddenError extends HttpError {
  statusCode = 403;
  name = "ForbiddenError";
}

/**
 * A precondition the user can resolve themselves — connect an integration, finish a setup step,
 * ask an admin to enable a feature. The message is the product telling them what to do next.
 *
 * `createServerAction` logs these and returns the message, but does **not** report them to Sentry.
 * They are product states, not defects: one fires for every user who has not done the setup yet,
 * so capturing them buries real errors under a permanent, self-inflicted baseline.
 *
 * Carries 403 so any HTTP surface that maps `HttpError.statusCode` keeps its current behavior.
 * Use `ForbiddenError` when the user genuinely lacks permission and cannot fix it themselves.
 */
export class ExpectedActionError extends HttpError {
  statusCode = 403;
  name = "ExpectedActionError";
}

export class NotFoundError extends HttpError {
  statusCode = 404;
  name = "NotFoundError";
}

export class ConflictError extends HttpError {
  statusCode = 409;
  name = "ConflictError";
}

export class InternalServerError extends HttpError {
  statusCode = 500;
  name = "InternalServerError";
}
